import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    ArrowForwardRounded,
    CheckRounded,
    ContentCopyRounded,
    GroupsOutlined,
    OpenInNewRounded,
} from "@mui/icons-material";
import utils from "../../universal/utils";
import { buildReferralInviteLink } from "../../universal/referralLinks";
import { toast } from "../../universal/toast";
import { isAllowedPayPalApprovalUrl } from "../membership/membershipPaypalUtils";
import Tooltip from "../common/Tooltip";
import {
    BILLING_CYCLES,
    CURRENCY,
    PAYMENT_METHODS,
    SUBSCRIPTION_STATUS,
    TIERS,
    TIER_DISPLAY,
    TIER_PRICES,
    YEARLY_RENEWAL_MODE,
} from "../../constants/membership";
import { colors } from "../../theme/colors";
import { DIALOG_DECLINE_BUTTON_SX } from "../common/dialogActionButtonSx";
import SubscriptionCheckoutDialog from "./SubscriptionCheckoutDialog.jsx";
import {
    getCurrentTier,
    getPlanAction,
    getPlanPricing,
} from "./subscriptionPageUtils";
import {
    ACCOUNT_PAGE_MAIN_CONTENT_SX,
    ACCOUNT_PAGE_SECTION_HEADER_SX,
    ACCOUNT_PAGE_SECTION_SX,
    ACCOUNT_PAGE_TITLE_SX,
} from "./accountPageStyles";
import * as S from "./subscriptionPageStyles";

const basePath = utils.getEdgeBasePath();
const CURRENCY_SYMBOL = CURRENCY === "EUR" ? "\u20AC" : "$";
const RENEWAL_SEGMENTS = 14;

const PLAN_CONFIG = [
    { tier: TIERS.PREMIUM, name: "Premium", pro: false },
    { tier: TIERS.PRO, name: "Pro", pro: true },
];

function formatCurrency(value) {
    return `${CURRENCY_SYMBOL}${Number(value || 0).toFixed(2)}`;
}

function formatMembershipDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

function SubscriptionPage({ user, membership, actions, onSectionChange }) {
    const [billingCycle, setBillingCycle] = useState(BILLING_CYCLES.YEARLY);
    const [founderProgress, setFounderProgress] = useState(null);
    const [referralCode, setReferralCode] = useState("");
    const [referralLoading, setReferralLoading] = useState(true);
    const [copiedField, setCopiedField] = useState(null);
    const [checkoutSelection, setCheckoutSelection] = useState(null);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState("");
    const [bankTransferData, setBankTransferData] = useState(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [cancelError, setCancelError] = useState("");
    const [cancelSuccess, setCancelSuccess] = useState("");
    const copyResetTimerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        fetch(`${basePath}/api/v1/founder/progress`, {
            credentials: "include",
        })
            .then((response) => response.json())
            .then((data) => {
                if (!cancelled && data?.ok) setFounderProgress(data);
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [user?.userId]);

    useEffect(() => {
        if (!user?.userId) {
            setReferralCode("");
            setReferralLoading(false);
            return undefined;
        }

        let cancelled = false;
        setReferralLoading(true);

        fetch(`${basePath}/api/v1/referral/my-code`, {
            credentials: "include",
        })
            .then((response) => response.json())
            .then((data) => {
                if (cancelled) return;
                if (data?.ok) {
                    setReferralCode(data.referralCode || data.code || "");
                }
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setReferralLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [user?.userId]);

    useEffect(
        () => () => {
            if (copyResetTimerRef.current) {
                clearTimeout(copyResetTimerRef.current);
            }
        },
        [],
    );

    const currentTier = getCurrentTier(membership);
    const currentTierInfo = TIER_DISPLAY[currentTier] || TIER_DISPLAY[TIERS.FREE];
    const isFree = currentTier === TIERS.FREE;
    const showFounderOffer = Boolean(
        isFree &&
        founderProgress?.spotsAvailable &&
        !founderProgress?.userIsFounder,
    );
    const founderSpotsRemaining = Number.isFinite(Number(founderProgress?.remaining))
        ? Math.max(0, Number(founderProgress.remaining))
        : null;
    const founderCapacity = Number.isFinite(Number(founderProgress?.cap))
        ? Math.max(0, Number(founderProgress.cap))
        : null;
    const founderDiscountPercent = Number(founderProgress?.discountPercent) || 0;
    const renewalCancelled = Boolean(
        cancelSuccess ||
            membership?.subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED,
    );
    const currentBillingCycle = membership?.billingCycle || null;
    const currentPricing = getPlanPricing({
        tier: currentTier,
        billingCycle: currentBillingCycle || BILLING_CYCLES.YEARLY,
        founderProgress,
    });
    const inviteLink = referralCode ? buildReferralInviteLink(referralCode) : "";
    const expiresAtLabel = formatMembershipDate(membership?.expiresAt);
    const daysRemaining = Number.isFinite(Number(membership?.daysRemaining))
        ? Math.max(0, Number(membership.daysRemaining))
        : null;
    const cycleDays = currentBillingCycle === BILLING_CYCLES.MONTHLY ? 30 : 365;
    const renewalFilledSegments = daysRemaining == null
        ? 0
        : Math.max(
              1,
              Math.min(
                  RENEWAL_SEGMENTS,
                  Math.round((daysRemaining / cycleDays) * RENEWAL_SEGMENTS),
              ),
          );
    const renewalColor = daysRemaining != null && daysRemaining <= 7
        ? colors.state.danger.main
        : daysRemaining != null && daysRemaining <= 30
            ? colors.primary.main
            : colors.state.success.main;
    const canCancelMonthly = Boolean(
        !isFree &&
        !membership?.grantedByAdmin &&
        currentBillingCycle === BILLING_CYCLES.MONTHLY &&
        !renewalCancelled,
    );
    const canRenewYearly = Boolean(
        !isFree &&
        currentBillingCycle === BILLING_CYCLES.YEARLY &&
        membership?.yearlyRenewalEligible,
    );

    const currentPriceText = useMemo(() => {
        if (isFree) return "No active subscription";
        const cadence = currentBillingCycle === BILLING_CYCLES.MONTHLY
            ? "billed monthly"
            : "billed yearly";
        return `${formatCurrency(currentPricing.monthlyAmount)}/mo · ${cadence}`;
    }, [currentBillingCycle, currentPricing.monthlyAmount, isFree]);

    const openCheckout = (planTier, renewalMode = null, cycle = billingCycle) => {
        setCheckoutError("");
        setBankTransferData(null);
        setCheckoutSelection({ planTier, renewalMode, billingCycle: cycle });
    };

    const closeCheckout = () => {
        if (checkoutLoading) return;
        setCheckoutSelection(null);
        setBankTransferData(null);
        setCheckoutError("");
    };

    const setCheckoutCycle = (cycle) => {
        if (checkoutLoading) return;
        setCheckoutError("");
        setBankTransferData(null);
        setCheckoutSelection((selection) => selection
            ? { ...selection, billingCycle: cycle }
            : selection);
    };

    const handleSubscribe = async (paymentMethod) => {
        if (!user?.userId || !checkoutSelection || checkoutLoading) return;

        const { planTier, renewalMode, billingCycle: checkoutCycle } = checkoutSelection;
        setCheckoutLoading(true);
        setCheckoutError("");

        try {
            const returnParams = new URLSearchParams({
                tier: planTier,
                billingCycle: checkoutCycle,
                paymentMethod,
            });
            if (renewalMode) returnParams.set("renewalMode", renewalMode);

            const response = await fetch(`${basePath}/api/v1/membership/subscribe`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    tier: planTier,
                    paymentMethod,
                    billingCycle: checkoutCycle,
                    renewalMode,
                    returnUrl: `${window.location.origin}/membership/success?${returnParams.toString()}`,
                    cancelUrl: `${window.location.origin}/membership/cancel?${returnParams.toString()}`,
                }),
            });
            const data = await response.json().catch(() => null);

            if (!response.ok || !data?.ok) {
                throw new Error(data?.message || "Failed to start subscription");
            }

            if (paymentMethod === PAYMENT_METHODS.PAYPAL) {
                if (!data.approvalUrl || !isAllowedPayPalApprovalUrl(data.approvalUrl)) {
                    throw new Error("Received an invalid PayPal approval URL");
                }
                window.location.assign(data.approvalUrl);
                return;
            }

            setBankTransferData(data);
            setCheckoutLoading(false);
        } catch (error) {
            setCheckoutError(error?.message || "Unable to start checkout");
            setCheckoutLoading(false);
        }
    };

    const handleCancelRenewal = async () => {
        if (!user?.userId || cancelLoading) return;

        setCancelLoading(true);
        setCancelError("");
        try {
            const response = await fetch(`${basePath}/api/v1/membership/cancel`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
            });
            const data = await response.json().catch(() => null);
            if (!response.ok || !data?.ok) {
                throw new Error(data?.message || "Failed to cancel renewal");
            }

            const message = expiresAtLabel
                ? `Renewal cancelled. Your ${currentTierInfo.name} access remains active until ${expiresAtLabel}.`
                : "Renewal cancelled. Your access remains active through the current period.";
            setCancelSuccess(message);
            setCancelDialogOpen(false);
            actions?.fetchMembershipStatus?.(user.userId);
            toast.success("Automatic renewal cancelled");
        } catch (error) {
            setCancelError(error?.message || "Unable to cancel renewal");
        } finally {
            setCancelLoading(false);
        }
    };

    const handleCopy = (value, field) => {
        if (!value || typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
            toast.error("Clipboard access is unavailable");
            return;
        }

        navigator.clipboard.writeText(value).then(() => {
            setCopiedField(field);
            toast.success(
                field === "code"
                    ? "Referral code copied"
                    : field === "link"
                        ? "Invite link copied"
                        : "Copied to clipboard",
            );
            if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
            copyResetTimerRef.current = setTimeout(() => setCopiedField(null), 1600);
        }).catch(() => {
            toast.error("Failed to copy. Please try again.");
        });
    };

    const checkoutConfig = checkoutSelection
        ? PLAN_CONFIG.find((plan) => plan.tier === checkoutSelection.planTier)
        : null;
    const checkoutPricing = checkoutSelection
        ? getPlanPricing({
              tier: checkoutSelection.planTier,
              billingCycle: checkoutSelection.billingCycle,
              founderProgress,
          })
        : null;
    const checkoutUsesFounderRate = Boolean(checkoutPricing?.founderApplied);

    return (
        <Box sx={ACCOUNT_PAGE_MAIN_CONTENT_SX}>
            <Box sx={ACCOUNT_PAGE_SECTION_SX}>
                <Box sx={ACCOUNT_PAGE_SECTION_HEADER_SX}>
                    <Typography
                        component="h1"
                        sx={ACCOUNT_PAGE_TITLE_SX}
                    >
                        Subscriptions
                    </Typography>
                </Box>

                <Box sx={S.SUBSCRIPTION_STATUS_BAND_SX} aria-label="Subscription overview">
                    <Box sx={S.SUBSCRIPTION_STATUS_PRIMARY_ZONE_SX}>
                        <Typography sx={S.SUBSCRIPTION_EYEBROW_SX}>Current plan</Typography>
                        <Box sx={S.SUBSCRIPTION_PLAN_LINE_SX}>
                            <Typography sx={S.SUBSCRIPTION_CURRENT_PLAN_SX}>
                                {currentTierInfo.name}
                            </Typography>
                            {membership?.grantedByAdmin ? (
                                <Box component="span" sx={S.SUBSCRIPTION_GRANTED_CHIP_SX}>
                                    Granted
                                </Box>
                            ) : null}
                        </Box>
                        <Typography sx={S.SUBSCRIPTION_STATUS_DETAIL_SX}>
                            {currentPriceText}
                        </Typography>
                    </Box>

                    <Box sx={S.SUBSCRIPTION_STATUS_DIVIDER_SX} aria-hidden />

                    <Box sx={S.SUBSCRIPTION_STATUS_ZONE_SX}>
                        <Typography sx={S.SUBSCRIPTION_EYEBROW_SX}>Renewal</Typography>
                        {isFree ? (
                            <Typography sx={{ ...S.SUBSCRIPTION_STATUS_DETAIL_SX, mt: "0.8rem" }}>
                                No active subscription
                            </Typography>
                        ) : (
                            <>
                                <Box sx={S.SUBSCRIPTION_RENEWAL_LABELS_SX}>
                                    <Box component="span">
                                        {currentBillingCycle === BILLING_CYCLES.MONTHLY && !renewalCancelled
                                            ? "Renews"
                                            : "Access ends"}
                                        {daysRemaining != null ? (
                                            <Box component="strong" sx={{ ml: "0.3rem", color: colors.accent.soft }}>
                                                in {daysRemaining} {daysRemaining === 1 ? "day" : "days"}
                                            </Box>
                                        ) : null}
                                    </Box>
                                    <Box component="span" sx={{ color: colors.text.muted, whiteSpace: "nowrap" }}>
                                        {expiresAtLabel}
                                    </Box>
                                </Box>
                                <Box sx={S.SUBSCRIPTION_RENEWAL_TRACK_SX} aria-hidden>
                                    {Array.from({ length: RENEWAL_SEGMENTS }).map((_, index) => (
                                        <Box
                                            key={`renewal-${index}`}
                                            sx={{
                                                ...S.SUBSCRIPTION_RENEWAL_SEGMENT_SX,
                                                background: index < renewalFilledSegments
                                                    ? `linear-gradient(90deg, ${alpha(renewalColor, 0.55)}, ${renewalColor})`
                                                    : undefined,
                                            }}
                                        />
                                    ))}
                                </Box>
                                {canCancelMonthly ? (
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => {
                                            setCancelError("");
                                            setCancelDialogOpen(true);
                                        }}
                                        sx={S.SUBSCRIPTION_INLINE_ACTION_SX}
                                    >
                                        Cancel automatic renewal
                                    </Box>
                                ) : canRenewYearly ? (
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => {
                                            setBillingCycle(BILLING_CYCLES.YEARLY);
                                            openCheckout(
                                                currentTier,
                                                YEARLY_RENEWAL_MODE,
                                                BILLING_CYCLES.YEARLY,
                                            );
                                        }}
                                        sx={S.SUBSCRIPTION_INLINE_ACTION_SX}
                                    >
                                        Renew yearly plan
                                    </Box>
                                ) : null}
                            </>
                        )}
                    </Box>
                </Box>

                {cancelSuccess ? <Alert severity="success" sx={{ mb: "1.25rem" }}>{cancelSuccess}</Alert> : null}

                {showFounderOffer ? (
                    <Box
                        component="aside"
                        aria-label="Founder badge availability"
                        sx={S.SUBSCRIPTION_FOUNDER_OFFER_SX}
                    >
                        <Box sx={S.SUBSCRIPTION_FOUNDER_COPY_SX}>
                            <Typography sx={S.SUBSCRIPTION_FOUNDER_EYEBROW_SX}>
                                <Box component="span" aria-hidden />
                                Founder allocation open
                            </Typography>
                            <Typography sx={S.SUBSCRIPTION_FOUNDER_DESCRIPTION_SX}>
                                Permanent numbered badge included with your first paid plan.{" "}
                                {founderDiscountPercent > 0 ? (
                                    <Box
                                        component="span"
                                        sx={S.SUBSCRIPTION_FOUNDER_DISCOUNT_SX}
                                    >
                                        Annual Pro · {founderDiscountPercent}% off
                                    </Box>
                                ) : null}
                            </Typography>
                        </Box>

                        <Box sx={S.SUBSCRIPTION_FOUNDER_AVAILABILITY_SX}>
                            <Typography component="span" sx={S.SUBSCRIPTION_FOUNDER_COUNT_SX}>
                                {founderSpotsRemaining == null
                                    ? "Limited"
                                    : founderSpotsRemaining.toLocaleString("en-US")}
                            </Typography>
                            <Typography component="span" sx={S.SUBSCRIPTION_FOUNDER_COUNT_LABEL_SX}>
                                {founderCapacity
                                    ? `available of ${founderCapacity.toLocaleString("en-US")}`
                                    : "spots available"}
                            </Typography>
                        </Box>
                    </Box>
                ) : null}

                <Box sx={S.SUBSCRIPTION_SECTION_HEADING_SX}>
                    <Typography component="h2" sx={S.SUBSCRIPTION_SECTION_TITLE_SX}>
                        Plans
                    </Typography>
                    <Box sx={S.SUBSCRIPTION_CYCLE_TOGGLE_SX} aria-label="Billing cycle">
                        <Box
                            aria-hidden
                            sx={S.getSubscriptionCycleIndicatorSx(
                                billingCycle === BILLING_CYCLES.YEARLY,
                            )}
                        />
                        <Box
                            component="button"
                            type="button"
                            aria-pressed={billingCycle === BILLING_CYCLES.MONTHLY}
                            onClick={() => setBillingCycle(BILLING_CYCLES.MONTHLY)}
                            sx={S.getSubscriptionCycleButtonSx(
                                billingCycle === BILLING_CYCLES.MONTHLY,
                            )}
                        >
                            Monthly
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            aria-pressed={billingCycle === BILLING_CYCLES.YEARLY}
                            onClick={() => setBillingCycle(BILLING_CYCLES.YEARLY)}
                            sx={S.getSubscriptionCycleButtonSx(
                                billingCycle === BILLING_CYCLES.YEARLY,
                            )}
                        >
                            Annual
                        </Box>
                    </Box>
                </Box>

                {founderProgress?.userIsFounder && founderProgress?.founderNumber ? (
                    <Box sx={S.SUBSCRIPTION_FOUNDER_NOTICE_SX}>
                        Founder #{founderProgress.founderNumber}
                    </Box>
                ) : null}

                <Box sx={S.SUBSCRIPTION_PLANS_GRID_SX}>
                    {PLAN_CONFIG.map((plan) => {
                        const pricing = getPlanPricing({
                            tier: plan.tier,
                            billingCycle,
                            founderProgress,
                        });
                        const action = getPlanAction({
                            currentTier,
                            planTier: plan.tier,
                            billingCycle,
                            currentBillingCycle,
                            yearlyRenewalEligible: membership?.yearlyRenewalEligible,
                        });
                        const buttonDisabled = action.kind === "current" || action.kind === "included";
                        const actionLabel = action.kind === "renew"
                            ? `Renew ${plan.name}`
                            : action.label;
                        const isAnnual = billingCycle === BILLING_CYCLES.YEARLY;
                        const monthlyListPrice = Number(
                            TIER_PRICES[plan.tier]?.[BILLING_CYCLES.MONTHLY] || 0,
                        );
                        const effectiveSavings = isAnnual && monthlyListPrice
                            ? Math.round(
                                ((monthlyListPrice - pricing.monthlyAmount) / monthlyListPrice) * 100,
                            )
                            : 0;

                        return (
                            <Box
                                key={plan.tier}
                                component="article"
                                sx={plan.pro
                                    ? [S.SUBSCRIPTION_PLAN_CARD_SX, S.SUBSCRIPTION_PRO_CARD_SX]
                                    : [S.SUBSCRIPTION_PLAN_CARD_SX, S.SUBSCRIPTION_PREMIUM_CARD_SX]}
                            >
                                <Box sx={S.SUBSCRIPTION_PLAN_NAME_ROW_SX}>
                                    <Typography component="h3" sx={S.SUBSCRIPTION_PLAN_NAME_SX}>
                                        {plan.name}
                                    </Typography>
                                </Box>

                                <Box sx={S.SUBSCRIPTION_PRICE_BLOCK_SX}>
                                    <Box sx={S.SUBSCRIPTION_PRICE_ROW_SX}>
                                        <Typography component="span" sx={S.SUBSCRIPTION_PRICE_SX}>
                                            {formatCurrency(pricing.monthlyAmount)}
                                        </Typography>
                                        <Typography component="span" sx={S.SUBSCRIPTION_PRICE_CADENCE_SX}>
                                            / month
                                        </Typography>
                                        {isAnnual ? (
                                            <Typography
                                                component="span"
                                                sx={S.SUBSCRIPTION_PRICE_COMPARE_SX}
                                            >
                                                {formatCurrency(monthlyListPrice)}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <Box sx={S.SUBSCRIPTION_BILLING_META_SX}>
                                        <Typography sx={S.SUBSCRIPTION_PRICE_NOTE_SX}>
                                            {isAnnual
                                                ? `Billed ${formatCurrency(pricing.yearlyTotal)} annually`
                                                : "Billed monthly"}
                                        </Typography>
                                        {isAnnual ? (
                                            <Box component="span" sx={S.SUBSCRIPTION_DISCOUNT_SX}>
                                                Save {effectiveSavings}%
                                            </Box>
                                        ) : null}
                                    </Box>
                                    <Box
                                        component="button"
                                        type="button"
                                        disabled={buttonDisabled}
                                        onClick={() => {
                                            if (action.kind === "renew") {
                                                openCheckout(plan.tier, YEARLY_RENEWAL_MODE);
                                            } else if (action.kind === "upgrade") {
                                                openCheckout(plan.tier);
                                            }
                                        }}
                                        sx={S.SUBSCRIPTION_PRIMARY_BUTTON_SX}
                                    >
                                        {buttonDisabled ? (
                                            <CheckRounded sx={{ fontSize: "1rem", mr: "0.35rem", verticalAlign: "middle" }} />
                                        ) : null}
                                        {actionLabel}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={S.SUBSCRIPTION_COMPARE_ROW_SX}>
                    <Box
                        component="a"
                        href="https://www.ouro.is/pricing"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={S.SUBSCRIPTION_COMPARE_LINK_SX}
                    >
                        See the full plan comparison
                        <OpenInNewRounded aria-hidden />
                    </Box>
                </Box>

                <Box sx={S.SUBSCRIPTION_REFERRAL_SX}>
                    <Box sx={S.SUBSCRIPTION_REFERRAL_HEADER_SX}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography component="h2" sx={S.SUBSCRIPTION_REFERRAL_TITLE_SX}>
                                <GroupsOutlined aria-hidden />
                                Invite friends, earn free months
                            </Typography>
                            <Typography sx={S.SUBSCRIPTION_REFERRAL_DESCRIPTION_SX}>
                                When a friend subscribes with your code, you both receive one free month.
                            </Typography>
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => onSectionChange?.("referral-program")}
                            sx={S.SUBSCRIPTION_REFERRAL_LINK_SX}
                        >
                            View referral program
                            <ArrowForwardRounded aria-hidden />
                        </Box>
                    </Box>

                    <Box sx={S.SUBSCRIPTION_REFERRAL_FIELDS_SX}>
                        {[
                            {
                                id: "code",
                                label: "Your code",
                                value: referralLoading ? "Loading..." : referralCode || "Unavailable",
                                copyValue: referralCode,
                            },
                            {
                                id: "link",
                                label: "Invite link",
                                value: referralLoading ? "Loading..." : inviteLink || "Unavailable",
                                copyValue: inviteLink,
                            },
                        ].map((field) => (
                            <Box key={field.id} sx={S.SUBSCRIPTION_REFERRAL_FIELD_SX}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={S.SUBSCRIPTION_EYEBROW_SX}>
                                        {field.label}
                                    </Typography>
                                    <Typography sx={{ ...S.SUBSCRIPTION_REFERRAL_VALUE_SX, mt: "0.35rem" }}>
                                        {field.value}
                                    </Typography>
                                </Box>
                                <Tooltip title={`Copy ${field.label.toLowerCase()}`} placement="top">
                                    <Box
                                        component="button"
                                        type="button"
                                        aria-label={`Copy ${field.label.toLowerCase()}`}
                                        disabled={!field.copyValue}
                                        onClick={() => handleCopy(field.copyValue, field.id)}
                                        sx={{
                                            ...S.SUBSCRIPTION_COPY_BUTTON_SX,
                                            color: copiedField === field.id
                                                ? colors.state.success.main
                                                : colors.primary.main,
                                        }}
                                    >
                                        {copiedField === field.id ? <CheckRounded /> : <ContentCopyRounded />}
                                    </Box>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            <SubscriptionCheckoutDialog
                open={Boolean(checkoutSelection)}
                planName={checkoutConfig?.name}
                renewal={Boolean(checkoutSelection?.renewalMode)}
                billingCycle={checkoutSelection?.billingCycle || BILLING_CYCLES.YEARLY}
                onBillingCycleChange={setCheckoutCycle}
                pricing={checkoutPricing}
                founderApplied={checkoutUsesFounderRate}
                bankTransferData={bankTransferData}
                loading={checkoutLoading}
                error={checkoutError}
                copiedField={copiedField}
                onCopy={handleCopy}
                onPayPal={() => handleSubscribe(PAYMENT_METHODS.PAYPAL)}
                onBankTransfer={() => handleSubscribe(PAYMENT_METHODS.BANK_TRANSFER)}
                onClose={closeCheckout}
                formatCurrency={formatCurrency}
            />

            <Dialog
                open={cancelDialogOpen}
                onClose={() => !cancelLoading && setCancelDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{ paper: { sx: S.SUBSCRIPTION_DIALOG_PAPER_SX } }}
            >
                <DialogTitle sx={{ fontWeight: 750 }}>Cancel automatic renewal?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: colors.text.secondary, fontSize: "0.82rem", lineHeight: 1.55 }}>
                        Future PayPal payments will stop. Your {currentTierInfo.name} access remains active
                        {expiresAtLabel ? ` until ${expiresAtLabel}` : " through the current billing period"}.
                    </Typography>
                    {cancelError ? <Alert severity="error" sx={{ mt: "1rem" }}>{cancelError}</Alert> : null}
                </DialogContent>
                <DialogActions sx={{ px: "1.5rem", pb: "1.25rem", gap: "0.65rem" }}>
                    <Button
                        disabled={cancelLoading}
                        onClick={() => setCancelDialogOpen(false)}
                        sx={{ color: colors.text.secondary }}
                    >
                        Keep membership
                    </Button>
                    <Button
                        variant="contained"
                        disabled={cancelLoading}
                        onClick={handleCancelRenewal}
                        sx={DIALOG_DECLINE_BUTTON_SX}
                    >
                        {cancelLoading ? <CircularProgress size={19} color="inherit" /> : "Cancel renewal"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

SubscriptionPage.propTypes = {
    user: PropTypes.object,
    membership: PropTypes.object,
    actions: PropTypes.object,
    onSectionChange: PropTypes.func,
};

export default React.memo(SubscriptionPage);
